import React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLeadModal } from '../../lib/leadModalContext';

const SCHEDULE = {
  kids: [
    { day: 'Tuesday', times: ['4:30 PM — Little Dragons (Ages 4–6)', '5:15 PM — Kids Karate (Ages 7–12)'] },
    { day: 'Thursday', times: ['4:30 PM — Little Dragons (Ages 4–6)', '5:15 PM — Kids Karate (Ages 7–12)'] },
  ],
  teens: [
    { day: 'Tuesday', times: ['6:00 PM — Teen Kempo (Ages 13–17)'] },
    { day: 'Thursday', times: ['6:00 PM — Teen Kempo (Ages 13–17)'] },
  ],
  adult: [
    { day: 'Tuesday', times: ['7:00 PM — Adult Kempo'] },
    { day: 'Thursday', times: ['7:00 PM — Adult Kempo'] },
  ],
};

const ScheduleBlock = ({ rows }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {rows.map((row) => (
      <div
        key={row.day}
        className="panel rounded-2xl p-4 hover:border-[color:var(--neon-red)]/40 transition-colors"
        data-testid={`schedule-${row.day.toLowerCase()}`}
      >
        <div className="flex items-center justify-between">
          <div className="font-display uppercase tracking-[0.14em] text-xs text-white">
            {row.day}
          </div>
          <Clock className="h-3.5 w-3.5 text-white/50" />
        </div>
        <ul className="mt-2 space-y-1.5">
          {row.times.map((t) => (
            <li key={t} className="text-sm text-white/80 font-mono-ui">{t}</li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const Schedule = () => {
  const { openLeadModal } = useLeadModal();
  return (
    <section id="schedule" className="relative py-16 sm:py-20" data-testid="schedule-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="font-display uppercase tracking-[0.22em] text-[11px] text-[color:var(--neon-red)]">
              Class Schedule
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-white tracking-tight">
              Pick a class. Get on the mat.
            </h2>
            <p className="mt-3 text-white/70 text-sm">
              Times shown are typical class slots at our Revolution location. We’ll match
              you to the best fit during your free trial.
            </p>
          </div>
          <Button
            onClick={() => openLeadModal('schedule')}
            className="w-full md:w-auto bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] font-display uppercase tracking-[0.14em] text-xs rounded-xl px-5 py-5 min-h-[48px] glow-red-sm"
            data-testid="schedule-book-trial-button"
          >
            Book Free Trial
          </Button>
        </div>

        <div className="mt-8">
          <Tabs defaultValue="kids">
            <TabsList className="bg-white/5 border border-white/10 h-auto p-1 flex w-full sm:w-fit">
              <TabsTrigger value="kids" className="flex-1 sm:flex-none font-display uppercase tracking-[0.14em] text-xs py-2.5 px-4 data-[state=active]:bg-[color:var(--neon-red)] data-[state=active]:text-white" data-testid="schedule-tab-kids">
                Kids
              </TabsTrigger>
              <TabsTrigger value="teens" className="flex-1 sm:flex-none font-display uppercase tracking-[0.14em] text-xs py-2.5 px-4 data-[state=active]:bg-[color:var(--neon-red)] data-[state=active]:text-white" data-testid="schedule-tab-teens">
                Teens
              </TabsTrigger>
              <TabsTrigger value="adult" className="flex-1 sm:flex-none font-display uppercase tracking-[0.14em] text-xs py-2.5 px-4 data-[state=active]:bg-[color:var(--neon-red)] data-[state=active]:text-white" data-testid="schedule-tab-adult">
                Adults
              </TabsTrigger>
            </TabsList>
            <TabsContent value="kids" className="mt-5"><ScheduleBlock rows={SCHEDULE.kids} /></TabsContent>
            <TabsContent value="teens" className="mt-5"><ScheduleBlock rows={SCHEDULE.teens} /></TabsContent>
            <TabsContent value="adult" className="mt-5"><ScheduleBlock rows={SCHEDULE.adult} /></TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
};

export default Schedule;
